import { SafeError } from '@benzinga/safe-await';
import {
  ListenableSubscribable,
  SOCKET_NO_ACTIVITY_MS,
  SubscribableReconnectingSocket,
  SubscribableReconnectingSocketEvent,
  Subscription,
} from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { AuthenticationManager } from '@benzinga/session';

import { QuoteSession } from './session';
import { QuotesV3Environment } from './environment';
import type { QuoteProtos } from './quotews_protos';
import { Security } from './entities';

type QuoteWSProtosModule = typeof import('./quotews_protos');

export interface NewQuoteEvent {
  quote: QuoteProtos.IQuote;
  type: 'quote';
}

export interface QuoteSocketErrorEvent {
  error: SafeError;
  errorType: 'quote:quote_socket_requires_you_to_be_logged_in';
  type: 'error';
}

export interface QuoteSocketReconnectEvent {
  type: 'reconnected';
}

export type QuoteSocketEvent = NewQuoteEvent | QuoteSocketErrorEvent | QuoteSocketReconnectEvent;

export class QuoteSocket extends ListenableSubscribable<QuoteSocketEvent> {
  private socket?: SubscribableReconnectingSocket<ArrayBuffer>;
  private socketSubscription?: Subscription<SubscribableReconnectingSocket<ArrayBuffer>>;
  private isAuthorized = false;
  private session: Session;
  private securityQueue: QuoteSession[] = [];
  private securityJoinQueue: QuoteSession[] = [];
  private securityDeleteList = new Map<QuoteSession, Date>();
  private authSubscription?: Subscription<AuthenticationManager>;
  private protosCache?: QuoteWSProtosModule;

  constructor(session: Session) {
    super();
    this.session = session;
  }

  public createSession = (securities: Security): QuoteSession => {
    return new QuoteSession(this, securities);
  };

  public updateSession = (session: QuoteSession): void => {
    if (this.getSocket().getState() === 'closed') {
      this.open();
    }

    if (!this.isAuthorized) {
      this.securityQueue.push(session);
    } else {
      this.updateSecuritiesFields(session);
    }
  };

  public endSession = (session: QuoteSession): void => {
    if (!this.isAuthorized) {
      this.securityQueue = this.securityQueue.filter(sec => sec.getSecurity() !== session.getSecurity());
    } else {
      this.leaveSecurities(session);
    }
  };

  public close = (): void => this.stop();

  private getSocket = () => {
    if (!this.socket) {
      this.socket = new SubscribableReconnectingSocket(
        this.session.getEnvironment(QuotesV3Environment).socketUrl,
        { binaryType: 'arraybuffer' },
        undefined,
        SOCKET_NO_ACTIVITY_MS,
      );
    }
    return this.socket;
  };

  private async getProtos(): Promise<QuoteWSProtosModule> {
    if (!this.protosCache) {
      this.protosCache = await import('./quotews_protos');
    }
    return this.protosCache;
  }

  private isIdSecurity = (security: Security): boolean => !!security && /^\d/.test(security);

  private updateSecuritiesFields = (session: QuoteSession): void => {
    this.securityDeleteList.delete(session);
    // We always want to join the security even if it was marked for deletion(it is already joined) as the session might have been updated
    this.securityJoinQueue = [...this.securityJoinQueue, session];
    // we want to bulk join securities. to do this we simply use the javascript event loop.
    setTimeout(async () => {
      if (this.securityJoinQueue?.length) {
        const protos = await this.getProtos();
        this.securityJoinQueue.forEach(security => {
          const subscribedFields = security.getFields();
          const symbol = security.getSecurity();
          if (!symbol || subscribedFields.size === 0 || this.isIdSecurity(symbol)) return;
          this.getSocket().send(
            protos.QuoteWSProtos.QuoteSocketRequest.encode({
              addUpdateQuoteSubscriptionRequest: {
                subscribeFields: Array.from(subscribedFields),
                symbol,
              },
            }).finish(),
          );
        });
        this.securityJoinQueue = [];
      }
    }, 0);
  };

  private leaveSecurities = (session: QuoteSession): void => {
    this.securityDeleteList.set(session, new Date());
    // we dont want to delete right away we want to debounce since joining and leaving is an expensive operation on the backend.
    setTimeout(async () => {
      // we want to bulk leave securities.
      const securitiesToDelete = Array.from(this.securityDeleteList.entries())
        .filter(([session, date]) => {
          if (date && Date.now() - date.getTime() > 4000) {
            this.securityDeleteList.delete(session);
            return true;
          }
          return false;
        })
        .map(([session]) => session);
      if (securitiesToDelete.length) {
        const protos = await this.getProtos();
        securitiesToDelete.forEach(session => {
          const symbol = session.getSecurity();
          if (this.isIdSecurity(symbol)) return;
          this.getSocket().send(
            protos.QuoteWSProtos.QuoteSocketRequest.encode({
              removeQuoteSubscriptionRequest: { symbol },
            }).finish(),
          );
        });
      }
    }, 5000);
  };

  private stop = (): void => {
    this.isAuthorized = false;
    this.getSocket().close();
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.authSubscription?.unsubscribe();
    this.authSubscription = undefined;
  };

  private open = (): void => {
    if (this.hasSubscribers()) {
      if (this.socketSubscription === undefined) {
        this.socketSubscription = this.getSocket().subscribe(this.onMessage);
      }
      this.getSocket().open();
    } else {
      this.stop();
    }
  };

  private onMessage = async (event: SubscribableReconnectingSocketEvent<ArrayBuffer>) => {
    const protos = await this.getProtos();
    switch (event.type) {
      case 'response': {
        const msg = protos.QuoteWSProtos.QuoteSocketResponse.decode(new Uint8Array(event.msg));
        switch (msg.messageType) {
          case 'PING':
            this.getSocket().send(protos.QuoteWSProtos.QuoteSocketRequest.encode({ PONG: {} }).finish());
            break;
          case 'Quote':
            if (msg.Quote?.quote) {
              this.dispatch({ quote: msg.Quote?.quote, type: 'quote' });
            }
            break;
          case 'UnsubscribeQuotesResponse':
          case 'SubscribeQuotesResponse':
            break;
        }
        break;
      }
      case 'open':
        this.isAuthorized = true;
        this.securityQueue.forEach(a => this.updateSecuritiesFields(a));
        this.securityQueue = [];
        break;
      case 'reconnected':
        this.securityQueue = [];
        this.isAuthorized = true;
        this.dispatch({ type: 'reconnected' });
        break;
    }
  };
}
